import ClickTracker from '@/components/Common/ClickTracker';
import IconCommon from '@/components/Common/DesignSystem/Icon/IconCommon';
import { ILogFields } from '@/services/TrackerService/Kinesis/interfaces';
import { NolPromotionStoreCtx } from '@/stores/NolPromotionStore';
import IntersectionObserver from '@researchgate/react-intersection-observer';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import React, { FC, PropsWithChildren, ReactNode, useCallback, useContext, useEffect, useRef } from 'react';

import styles from './index.module.scss';

interface Props {
  type: 'gray' | 'white';
  supTitle?: ReactNode;
  title: ReactNode;
  image: ReactNode;
  descriptionTitle?: ReactNode;
  hideSeparatorMargin?: boolean;
  description?: { bold?: string; normal?: string };
  descriptions?: {
    bold?: boolean;
    normal?: boolean;
    text: string | JSX.Element;
    hideDotPrefix?: boolean;
    marginBottom?: boolean;
    indent?: number;
  }[];
  onDetailClick?: () => void;
  link?: {
    onLinkClick?: () => void;
    onLinkTitle?: string;
  };
  className?: string;
  onCardMount?: (el: HTMLDivElement | null) => void;
  logData: Partial<ILogFields>;
}
const AnimatedCard: FC<PropsWithChildren<Props>> = ({
  type,
  supTitle,
  title,
  image,
  descriptionTitle,
  description,
  descriptions,
  onDetailClick,
  link,
  className,
  onCardMount,
  logData,
  children,
}) => {
  const store = useContext(NolPromotionStoreCtx);
  const { onLinkClick, onLinkTitle } = link || {};

  const containerRef = useRef<HTMLDivElement>(null);
  const [playAnimation, setPlayAnimation] = React.useState(false);
  const onChange = useCallback((entry: IntersectionObserverEntry) => {
    if (entry.intersectionRatio >= 0.15) {
      setPlayAnimation(true);
      // unobserve();
    }
  }, []);

  useEffect(() => {
    onCardMount?.(containerRef.current);
  }, []);

  return (
    <IntersectionObserver onChange={onChange} threshold={0.15}>
      <div
        className={clsx([
          className,
          type === 'gray' ? styles.grayContainer : styles.whiteContainer,
          playAnimation ? styles.show : '',
        ])}
        ref={containerRef}
      >
        {supTitle ?? <div>{supTitle}</div>}
        <div>{title}</div>
        <div>{image}</div>
        {descriptionTitle ?? <div>{descriptionTitle}</div>}
        <div className={styles.description}>
          {children ? (
            children // children이 있을 때 우선 처리
          ) : (
            <>
              {description?.bold ? <div className={styles.descriptionMajor}>{description.bold}</div> : ''}
              {description?.normal ? <div className={styles.descriptionSub}>{description.normal}</div> : ''}
              {/*왼쪽 점이 붙은 설명 및 들여쓰기 처리를 위한 파라미터*/}
              {!!descriptions?.length && (
                <ul className={styles.descriptionUl}>
                  {descriptions.map((dotDescription, idx) => {
                    const dotPrefix = !dotDescription.hideDotPrefix;
                    const indent = dotDescription.indent ?? 0;
                    const marginLeft = indent ? `calc(${indent} * 0.4rem)` : '';
                    const isBold = dotDescription.bold ?? false;
                    const isNormal = dotDescription.normal ?? true;
                    const marginBottom = dotDescription.marginBottom ?? false;

                    const dotClassName = clsx(
                      isBold ? styles.descriptionMajor : '',
                      isNormal ? styles.descriptionSub : '',
                      styles.descriptions,
                      dotPrefix ? styles.dotDescriptions : '',
                      'dotDescriptions',
                      marginBottom ? styles.descriptionMarginBottom : '',
                    );
                    return (
                      <li key={`dot-description_${idx}`} className={dotClassName} style={{ marginLeft }}>
                        {dotDescription.text}
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </div>
        {!!onDetailClick && (
          <ClickTracker pageName={store.pageName} logData={logData}>
            <div className={styles.more} onClick={onDetailClick}>
              자세히 보기&nbsp;
              <IconCommon type="icon_arrowmore_fill" className={styles.moreIcon} />
            </div>
          </ClickTracker>
        )}
        {!!onLinkClick && !!onLinkTitle && (
          <ClickTracker pageName={store.pageName} logData={logData}>
            <div className={clsx(styles.more, 'link')} onClick={onLinkClick}>
              {onLinkTitle}&nbsp;
              <i className={styles.moreLinkIcon} />
            </div>
          </ClickTracker>
        )}
      </div>
    </IntersectionObserver>
  );
};

export default observer(AnimatedCard);
